import { ReactElement } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import opcImportIconPath from "assets/svg/opcImport.svg";
import opcImportCompleteIconPath from "assets/svg/opcImportComplete.svg";
import opcImportFailedIconPath from "assets/svg/opcImportFailed.svg";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import ActiveTagIcon from "components/svg-icons/active-tag-icon";
import {
  formatImportTimestamp,
  getLastImport,
  getLastImportedByName,
  isScheduleImportActive,
  isScheduleImported,
  isScheduleImportProcessing,
  shouldShowOpcImportFailure,
  TScheduleSourceInfo,
  TScheduleSourceInfoRow
} from "./schedules-in-opc-import-state";
import useTrackedOpcExports from "./use-opc-exports";

// Both assets are 15x16, so the box is square but object-contain keeps them from
// being stretched to fill it.
const ICON_CLASSNAME = "w-4 h-4 object-contain";

// Only one OPC import runs at a time, so while one row is importing every other
// row's control is inert and says why.
export const IMPORT_BLOCKED_TOOLTIP =
  "Import in progress. You can import this schedule once the current process has been completed.";

const IMPORT_PROCESSING_TOOLTIP = "Import in progress";

// The completed icon reports a state, but its control re-imports, so the hover
// has to say more than the icon does — OPC keeps publishing newer versions of a
// schedule already in CIQ, and nothing else in the row tells the user that
// clicking again is how they get one.
export const IMPORT_COMPLETE_TOOLTIP =
  "Schedule already imported. Reimport to update with the latest version from OPC.";

export const IMPORT_FAILED_MESSAGE = "Recent import failed. Please try again.";

// Refused outright rather than queued: the active import is the one CIQ is
// running the project off, so overwriting it is not something a wait resolves.
export const IMPORT_ACTIVE_TOOLTIP =
  "You can't import this schedule as it is currently active.";

const TOOLTIP_MAX_LINE_CHARS = 30;

/**
 * Wraps a tooltip to roughly 30 characters a line. antd otherwise lays a title
 * out on one line however long it is, so the sentence-length reasons above render
 * as a strip wider than the grid column they point at.
 *
 * A limit rather than a target: words are never split, so a line stops at the
 * last word that fits and a word longer than the limit takes a line of its own
 * and overruns it. Overrunning is the lesser evil — the alternative is a word
 * broken across two lines, which costs more to read than a slightly wide bubble.
 *
 * Returns a string rather than <br /> nodes so the title stays a single text
 * node: `whitespace-pre-line` below is what turns these newlines into breaks.
 */
export const wrapTooltipLines = (text: string): string =>
  text
    .split(/\s+/)
    .filter(Boolean)
    .reduce<Array<string>>((lines, word) => {
      const current = lines[lines.length - 1];
      const candidate = current === undefined ? word : `${current} ${word}`;
      // The first word starts the first line whatever its length, and any word
      // that no longer fits starts the next one.
      if (current === undefined || candidate.length > TOOLTIP_MAX_LINE_CHARS) {
        return [...lines, word];
      }
      lines[lines.length - 1] = candidate;
      return lines;
    }, [])
    .join("\n");

/**
 * The tooltip every state in this column hovers into, so the whole row reads the
 * same. Name and Description reach the identical thing through
 * TruncatedTextWithTooltip, which renders its text in a `text-xs p-0.5` box and
 * places it on top; a native `title` would be the odd one out — different delay,
 * different look, and no way to position it, which matters in a column pinned to
 * the frozen right edge.
 *
 * It always wraps a plain element rather than the control itself. A disabled
 * button emits no mouse events, so a tooltip anchored to one would be the only
 * tooltip a user can never read — the same reason the sync button wraps its
 * disabled Button in a span.
 *
 * Wrapping happens here rather than in the message constants so every state gets
 * it, including the labels OpcIconButton falls back to, and so the constants stay
 * plain sentences for the aria-labels that read the same text.
 */
function OpcCellTooltip({
  title,
  children
}: Readonly<{ title: string; children: ReactElement }>) {
  return (
    <Tooltip
      title={
        <div className="text-xs p-0.5 whitespace-pre-line">
          {wrapTooltipLines(title)}
        </div>
      }
      placement="top"
      destroyOnHidden
      autoAdjustOverflow
    >
      {children}
    </Tooltip>
  );
}

type OpcIconButtonProps = {
  iconPath: string;
  // Names the control for assistive tech, and is the hover tooltip too unless
  // one is given below.
  label: string;
  // Split out only where the hover has to explain more than the accessible name
  // should carry: a name is read aloud before every interaction, so it stays the
  // short one.
  tooltip?: string;
  onClick: () => void;
  buttonTestId: string;
  iconTestId: string;
  // Carries the disabled state and its explanation in one prop: this column only
  // ever greys a control out for a reason worth reading, so "disabled with
  // nothing to say" is not a state that should be expressible.
  disabledReason?: string;
  /** Archived projects: greyed out and disabled, with no hover tooltip. */
  isReadOnly?: boolean;
};

function OpcIconButton({
  iconPath,
  label,
  tooltip,
  onClick,
  buttonTestId,
  iconTestId,
  disabledReason,
  isReadOnly = false
}: Readonly<OpcIconButtonProps>) {
  if (isReadOnly)
    return (
      <div className="flex items-center leading-none">
        <button
          type="button"
          className="ciq-icon-button !p-1.5 disabled:opacity-40"
          disabled
          aria-label={label}
          data-testid={buttonTestId}
        >
          <img
            src={iconPath}
            alt=""
            className={ICON_CLASSNAME}
            data-testid={iconTestId}
          />
        </button>
      </div>
    );

  return (
    <div className="flex items-center leading-none">
      {/* The tooltip anchors to this span, not to the cell-filling div around it:
          a block-level trigger would arm the hover across the whole cell and
          centre the bubble on the cell rather than on the icon. inline-flex
          shrinks it to exactly the button's box. */}
      {/* The wait outranks whatever the control would otherwise say: while another
          import is running, why this one is greyed out is the only thing worth
          reading. */}
      <OpcCellTooltip title={disabledReason ?? tooltip ?? label}>
        <span className="inline-flex cursor-pointer">
          <button
            type="button"
            // Stays a pointer while greyed out rather than switching to
            // not-allowed: the blocked control's whole job is to be hovered, and a
            // no-entry cursor reads as "nothing here" just as the tooltip explaining
            // the wait is about to appear.
            className="ciq-icon-button !p-1.5 disabled:opacity-40 disabled:cursor-pointer"
            disabled={Boolean(disabledReason)}
            onClick={onClick}
            aria-label={label}
            data-testid={buttonTestId}
          >
            {/* Sized here rather than trusting the asset: this is an <img>, so the
              file's own width/height would otherwise drive the row height. alt is
              empty because the button already carries the accessible name. */}
            <img
              src={iconPath}
              alt=""
              className={ICON_CLASSNAME}
              data-testid={iconTestId}
            />
          </button>
        </span>
      </OpcCellTooltip>
    </div>
  );
}

/**
 * Sr No. only. The Active pill lives on the Name cell so it sits next to the
 * schedule title, matching the Schedules tab and the design screenshot.
 */
export function SrNoCellRenderer({
  data
}: Readonly<{ data?: TScheduleSourceInfoRow }>) {
  return <span data-testid="opc-sr-no">{data?.srNo ?? ""}</span>;
}

/**
 * Schedule name plus the Active pill when this OPC schedule is the one CIQ is
 * running the project off. The pill is an SVG chip rather than text so every
 * place that marks "active" shares the same artwork.
 */
export function NameCellRenderer({
  data,
  value
}: Readonly<{ data?: TScheduleSourceInfo; value?: string | null }>) {
  const name = value ?? data?.metadata?.name ?? "";
  return (
    <div className="flex items-center gap-1.5 min-w-0 leading-none py-0.5">
      <div className="min-w-0 flex-1 truncate">
        <TruncatedTextWithTooltip text={name} tooltipContent={name} />
      </div>
      {isScheduleImportActive(data) ? (
        <ActiveTagIcon className="shrink-0" />
      ) : null}
    </div>
  );
}

/**
 * Last Imported: the date on the first line, whoever ran the import on the
 * second — the two-line audit cell the submittal and material logs render for
 * "Last Updated by User", with the name where those put the time.
 *
 * Reads the row rather than the cell value: the value is the one-line
 * "MM-DD-YYYY by First Last" that the filter, the sort and the quick search all
 * still work off, and splitting that string back apart here would make the
 * layout depend on its punctuation.
 *
 * Both lines go through TruncatedTextWithTooltip, so a long name clips with a
 * hover like every other cell in this grid — the column's own renderer replaces
 * the one the page's defaultColDef would otherwise supply.
 */
export function LastImportedCellRenderer({
  data
}: Readonly<{ data?: TScheduleSourceInfo }>) {
  const importedOn = formatImportTimestamp(
    getLastImport(data)?.import_log?.created_at
  );
  // Nothing imported, or a timestamp that would not parse — an empty cell rather
  // than a lone name with no date to anchor it.
  if (!importedOn) return null;

  const importedBy = getLastImportedByName(data);

  return (
    <div className="leading-tight flex flex-col gap-1 pt-1 pr-1 w-full">
      <TruncatedTextWithTooltip
        text={importedOn}
        dataTestId="last-imported-on"
      />
      {/* Absent for an import whose user has since been removed: the date alone
          still says the schedule was imported, so the row keeps one line. */}
      {importedBy && (
        <div className="text-ciq-gray-800">
          <TruncatedTextWithTooltip
            text={importedBy}
            dataTestId="last-imported-by"
          />
        </div>
      )}
    </div>
  );
}

/**
 * What the page hands the Actions column through ag-grid's `context` prop.
 *
 * `isImportRunning` is derived from row data up on the page because a cell only
 * ever sees its own row, and `projectId` scopes the tracked-import store, which
 * is shared across every project open in this tab.
 *
 * `failedImportScheduleIds` / `recentlyProcessedIds` cover the window before a
 * refetch lands with the terminal import_log status on the row.
 */
export type TOpcActionsCellContext = {
  onImportSchedule: (row?: TScheduleSourceInfo) => void;
  projectId: string;
  isImportRunning: boolean;
  isProjectArchived?: boolean;
  failedImportScheduleIds: ReadonlySet<string>;
  /** Ids whose import finished PROCESSED this session (pre-refetch override). */
  recentlyProcessedIds: ReadonlySet<string>;
};

/**
 * Import action for an OPC schedule not yet imported into CIQ. The confirmation
 * and the POST live on the page — a cell renderer is destroyed and recreated on
 * every row-data refresh and on scroll virtualization, so an async continuation
 * started here would be orphaned.
 */
export function OpcImportActionCellRenderer({
  data,
  context,
  disabledReason
}: Readonly<{
  data?: TScheduleSourceInfo;
  context: TOpcActionsCellContext;
  disabledReason?: string;
}>) {
  return (
    <OpcIconButton
      iconPath={opcImportIconPath}
      label="Import Schedule"
      onClick={() => context.onImportSchedule(data)}
      buttonTestId="opc-import-button"
      iconTestId="opc-import-icon"
      disabledReason={disabledReason}
      isReadOnly={context.isProjectArchived}
    />
  );
}

/**
 * An OPC schedule already imported into CIQ. The icon reports that state, but the
 * control does exactly what the import action does: a schedule can be imported
 * again, and OPC keeps handing back newer versions of the same one. The whole
 * cycle from here is the un-imported one: confirm, POST, spinner, back to this
 * icon.
 *
 * Named by state rather than by verb, as asked; the hover is where the re-import
 * is spelled out, and the confirm dialog names the action before anything
 * happens.
 */
export function OpcImportCompleteCellRenderer({
  data,
  context,
  disabledReason
}: Readonly<{
  data?: TScheduleSourceInfo;
  context: TOpcActionsCellContext;
  disabledReason?: string;
}>) {
  return (
    <OpcIconButton
      iconPath={opcImportCompleteIconPath}
      label="Imported"
      tooltip={IMPORT_COMPLETE_TOOLTIP}
      onClick={() => context.onImportSchedule(data)}
      buttonTestId="opc-import-complete-button"
      iconTestId="opc-import-complete-icon"
      disabledReason={disabledReason}
      isReadOnly={context.isProjectArchived}
    />
  );
}

/**
 * Shown after a recent import ends in FAILED. Same OpcIconButton + hover
 * tooltip pattern as the other Actions states; the red asset and copy are what
 * mark the failure. While another import runs the control greys out like every
 * other Actions state.
 */
export function OpcImportFailedCellRenderer({
  data,
  context,
  disabledReason
}: Readonly<{
  data?: TScheduleSourceInfo;
  context: TOpcActionsCellContext;
  disabledReason?: string;
}>) {
  return (
    <OpcIconButton
      iconPath={opcImportFailedIconPath}
      label="Import failed"
      tooltip={IMPORT_FAILED_MESSAGE}
      onClick={() => context.onImportSchedule(data)}
      buttonTestId="opc-import-failed-button"
      iconTestId="opc-import-failed-icon"
      disabledReason={disabledReason}
      isReadOnly={context.isProjectArchived}
    />
  );
}

/**
 * The import is running. Padded to match OpcIconButton's box so the column does
 * not jump as a row moves between states.
 */
export function OpcImportProcessingCellRenderer() {
  return (
    <div className="flex items-center leading-none">
      {/* Same tooltip, same inline-flex trigger and same pointer cursor as the two
          icon states. The spinner is no more clickable than a greyed-out icon, but
          it is just as worth hovering, and a cell whose hover target and cursor
          change as the row moves between states reads as three different controls.
          p-1.5 sits here rather than on the div so the box matches the icon
          button's, which carries the same padding. */}
      <OpcCellTooltip title={IMPORT_PROCESSING_TOOLTIP}>
        <span
          className="inline-flex items-center p-1.5 cursor-pointer"
          role="status"
          aria-label={IMPORT_PROCESSING_TOOLTIP}
          data-testid="opc-import-processing"
        >
          <Spin indicator={<LoadingOutlined spin />} size="small" />
        </span>
      </OpcCellTooltip>
    </div>
  );
}

/**
 * Picks which of the Actions cell states to show.
 *
 * A container rather than a cellRendererSelector because the spinner also has to
 * react to the tracked-import store, and a selector only ever sees ag-grid
 * params — it does not re-run when a module store changes.
 *
 * Precedence is PROCESSING > FAILED > IMPORTED > NEW: a row mid-import must not
 * offer the action again, must not claim to be done, and must not look failed
 * under a spinner. The tracked handle covers the window before the import is
 * visible in row data; the row's own status covers everything after, including
 * an import another user started.
 */
export function OpcImportActionsCellRenderer(
  params: Readonly<{
    data?: TScheduleSourceInfo;
    context: TOpcActionsCellContext;
  }>
) {
  const trackedExports = useTrackedOpcExports();
  const { data, context } = params;

  const isImporting =
    Boolean(data?.id && trackedExports[data.id]) ||
    isScheduleImportProcessing(data);

  if (isImporting) return <OpcImportProcessingCellRenderer />;

  // Past this point the row itself is idle, so anything still running belongs to
  // a different row and this control has to wait for it.
  //
  // The two sources are read in two different places on purpose. The store is
  // read live here, through the same subscription that drives the spinner, so
  // every other cell greys out the instant an import starts rather than one
  // refetch later. Row-data PROCESSING — which is how an import another user
  // started shows up — can only come through context, and does not need to be
  // live: it changes with row data, and ag-grid applies a new context before it
  // dispatches the row-data change that refreshes these cells.
  const isAnotherImportRunning =
    Object.values(trackedExports).some(
      (handle) => handle.projectId === context.projectId
    ) || context.isImportRunning;

  const disabledReason = isAnotherImportRunning
    ? IMPORT_BLOCKED_TOOLTIP
    : undefined;

  if (
    shouldShowOpcImportFailure(data, context.failedImportScheduleIds, {
      recentlyProcessedIds: context.recentlyProcessedIds
    })
  ) {
    return (
      <OpcImportFailedCellRenderer
        {...params}
        disabledReason={disabledReason}
      />
    );
  }

  if (
    isScheduleImported(data) ||
    (data?.id && context.recentlyProcessedIds?.has(data.id))
  )
    return (
      <OpcImportCompleteCellRenderer
        {...params}
        // Being active outranks the wait. Both grey the icon out, but
        // IMPORT_BLOCKED_TOOLTIP promises the control comes back "once the
        // current process has been completed" — a promise this row cannot keep,
        // since it is refused for a reason no import finishing will change.
        disabledReason={
          isScheduleImportActive(data) ? IMPORT_ACTIVE_TOOLTIP : disabledReason
        }
      />
    );
  return (
    <OpcImportActionCellRenderer {...params} disabledReason={disabledReason} />
  );
}
